// src/admin/AdminClients.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';

function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Édition inline
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [saving, setSaving] = useState(false);

  // Suppression / blocage
  const [deletingId, setDeletingId] = useState(null);

  // Détail (modal)
  const [detailClient, setDetailClient] = useState(null);

  /* =========================================================
     CHARGEMENT
  ========================================================= */
  const fetchClients = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('auth_token');
      const { data } = await axios.get('https://madadline.com/api/users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError('Impossible de charger les clients.');
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClients(); }, []);

  /* =========================================================
     ÉDITION
  ========================================================= */
  const startEdit = (c) => {
    setEditingId(c.id);
    setEditName(c.name || '');
    setEditEmail(c.email || '');
    setEditPhone(c.phone || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditEmail('');
    setEditPhone('');
  };

  const saveEdit = async (id) => {
    if (!editName.trim() || !editEmail.trim()) {
      alert('Le nom et l\'email sont obligatoires.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `https://madadline.com/api/users/${id}`,
        { name: editName.trim(), email: editEmail.trim(), phone: editPhone.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClients(prev =>
        prev.map(c => c.id === id
          ? { ...c, name: editName.trim(), email: editEmail.trim(), phone: editPhone.trim() }
          : c
        )
      );
      cancelEdit();
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'Erreur lors de la modification.');
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     SUPPRESSION
  ========================================================= */
  const deleteClient = async (id) => {
    if (!window.confirm('Supprimer définitivement ce client ? Toutes ses commandes resteront dans l\'historique.')) return;
    setDeletingId(id);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`https://madadline.com/api/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(prev => prev.filter(c => c.id !== id));
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.error || 'Erreur lors de la suppression.');
    } finally {
      setDeletingId(null);
    }
  };

  /* =========================================================
     FILTRE
  ========================================================= */
  const filtered = clients.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q)
    );
  });

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="admin-clients">

      <header className="admin-page-head">
        <div>
          <h1>Clientes</h1>
          <p>{clients.length} cliente{clients.length > 1 ? 's' : ''}</p>
        </div>
        <button
          className="admin-btn admin-btn--ghost"
          onClick={fetchClients}
          disabled={loading}
        >
          ↻ Rafraîchir
        </button>
      </header>

      <div className="admin-filters">
        <input
          type="text"
          placeholder="Rechercher par nom, email ou téléphone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="admin-input"
        />
      </div>

      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : error ? (
        <p className="admin-empty" style={{ color: '#c00' }}>{error}</p>
      ) : filtered.length === 0 ? (
        <p className="admin-empty">Aucune cliente.</p>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Inscrite le</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const isEditing = editingId === c.id;
                const isDeleting = deletingId === c.id;

                return (
                  <tr key={c.id} style={isDeleting ? { opacity: 0.4 } : undefined}>
                    {/* Nom */}
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          className="admin-input"
                          style={{ minWidth: 140 }}
                        />
                      ) : (
                        <div className="admin-table__user">
                          <span className="admin-table__avatar">
                            {(c.name || 'C').charAt(0).toUpperCase()}
                          </span>
                          <span>{c.name || '—'}</span>
                        </div>
                      )}
                    </td>

                    {/* Email */}
                    <td>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editEmail}
                          onChange={e => setEditEmail(e.target.value)}
                          className="admin-input"
                          style={{ minWidth: 180 }}
                        />
                      ) : (
                        c.email || '—'
                      )}
                    </td>

                    {/* Téléphone */}
                    <td>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editPhone}
                          onChange={e => setEditPhone(e.target.value)}
                          className="admin-input"
                          style={{ minWidth: 130 }}
                        />
                      ) : (
                        c.phone || '—'
                      )}
                    </td>

                    {/* Date */}
                    <td>
                      {c.created_at
                        ? new Date(c.created_at).toLocaleDateString('fr-FR')
                        : '—'}
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <button
                            className="admin-btn admin-btn--primary"
                            onClick={() => saveEdit(c.id)}
                            disabled={saving}
                          >
                            {saving ? '…' : '✓ Enregistrer'}
                          </button>
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={cancelEdit}
                            disabled={saving}
                            style={{ marginLeft: 6 }}
                          >
                            Annuler
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={() => setDetailClient(c)}
                          >
                            👁 Voir
                          </button>
                          <button
                            className="admin-btn admin-btn--ghost"
                            onClick={() => startEdit(c)}
                            disabled={isDeleting}
                            style={{ marginLeft: 6 }}
                          >
                            ✏️
                          </button>
                          <button
                            className="admin-btn admin-btn--danger"
                            onClick={() => deleteClient(c.id)}
                            disabled={isDeleting}
                            style={{ marginLeft: 6 }}
                          >
                            {isDeleting ? '…' : '🗑'}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL DÉTAIL */}
      {detailClient && (
        <div
          className="admin-modal"
          onClick={() => setDetailClient(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 14, padding: 24,
              maxWidth: 500, width: '100%', maxHeight: '90vh', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: 20 }}>
                {detailClient.name || 'Cliente'}
              </h2>
              <button
                onClick={() => setDetailClient(null)}
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <p><strong>Email :</strong> {detailClient.email || '—'}</p>
            <p><strong>Téléphone :</strong> {detailClient.phone || '—'}</p>
            <p><strong>Ville :</strong> {detailClient.city || '—'}</p>
            <p><strong>Adresse :</strong> {detailClient.address || '—'}</p>
            <p>
              <strong>Inscrite le :</strong>{' '}
              {detailClient.created_at
                ? new Date(detailClient.created_at).toLocaleString('fr-FR')
                : '—'}
            </p>

            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button
                className="admin-btn admin-btn--ghost"
                onClick={() => setDetailClient(null)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminClients;